import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Services from '@/pages/home/components/Services';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://grainn-office.com';

const servicesJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/services#webpage`,
      url: `${siteUrl}/services`,
      name: '大阪 WEBサイト制作・SNS運用代行・デジタルマーケティング｜GRAINN サービス内容',
      description: 'GRAINNが提供するWEBサイト制作、SNS運用代行、MEO・SEO対策、デジタルマーケティング支援のサービス一覧。大阪の中小企業・個人事業主向けに初回無料相談受付中。',
      inLanguage: 'ja',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: 'サービス内容', item: `${siteUrl}/services` },
        ],
      },
    },
    {
      '@type': 'Service',
      '@id': `${siteUrl}/services#service-web`,
      name: 'WEBサイト制作',
      description: 'ランディングページ・コーポレートサイト・ECサイトなど、目的に合わせたWEBサイトを制作します。',
      provider: { '@id': `${siteUrl}/#business` },
      areaServed: '大阪府',
      offers: {
        '@type': 'Offer',
        priceCurrency: 'JPY',
        price: '50000',
        priceSpecification: {
          '@type': 'PriceSpecification',
          minPrice: '50000',
          priceCurrency: 'JPY',
        },
      },
    },
    {
      '@type': 'Service',
      '@id': `${siteUrl}/services#service-sns`,
      name: 'SNS運用代行',
      description: 'Instagram・X・Facebookなどのアカウント戦略設計から投稿制作・運用まで一括サポート。',
      provider: { '@id': `${siteUrl}/#business` },
      areaServed: '大阪府',
      offers: {
        '@type': 'Offer',
        priceCurrency: 'JPY',
        price: '19800',
        priceSpecification: {
          '@type': 'PriceSpecification',
          minPrice: '19800',
          priceCurrency: 'JPY',
        },
      },
    },
  ],
};

export default function ServicesPage() {
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <main className="min-h-screen">
      <title>大阪 WEBサイト制作・SNS運用代行・デジタルマーケティング｜GRAINN サービス内容</title>
      <meta name="description" content="GRAINNが提供するWEBサイト制作、SNS運用代行、MEO・SEO対策、デジタルマーケティング支援のサービス一覧。大阪の中小企業・個人事業主向けに初回無料相談受付中。" />
      <link rel="canonical" href={`${siteUrl}/services`} />
      <JsonLd data={servicesJsonLd} id="services" />
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-44 pb-24 overflow-hidden bg-[#1C2B22]">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=minimalist%20dark%20green%20creative%20agency%20workspace%20with%20laptop%20and%20design%20tools%20overhead%20flat%20lay%20professional%20clean%20desk%20with%20plants%20subtle%20lighting%20premium%20Japanese%20aesthetic&width=1920&height=600&seq=services-hero-bg&orientation=landscape"
            alt="サービス"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1C2B22]/80 to-[#1C2B22]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <p className="text-[#C9A878] text-xs tracking-[0.4em] mb-5 uppercase">/ Services</p>
          <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-tight mb-6">
            提供サービス
          </h1>
          <div className="w-16 h-px bg-[#C9A878]/60 mb-6" />
          <p className="text-white/60 text-lg max-w-xl leading-relaxed">
            WEBサイト制作からデジタルマーケティングまで、<br className="hidden md:block" />
            ビジネス成長を支えるサービスを提供します。
          </p>
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mt-8 text-xs text-white/30">
            <Link to="/" className="hover:text-white/60 transition-colors">ホーム</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white/60">サービス</span>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <Services />

      {/* CTA */}
      <section className="py-20 bg-[#F8F5EF]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C2B22] mb-5 tracking-tight">
            まずはお気軽にご相談ください
          </h2>
          <p className="text-[#666] mb-8 text-base leading-relaxed">
            初回相談は無料。サービスの詳細や費用感についても丁寧にご説明します。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#2E5540] text-white px-10 py-4 rounded-full text-sm tracking-widest hover:bg-[#1C3A2A] transition-colors whitespace-nowrap"
          >
            <i className="ri-mail-send-line" />
            無料相談を申し込む
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
